import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { geoCentroid, geoArea } from 'd3-geo';

/**
 * 3D sales globe (globe.gl / three.js). Drag = rotate 360 degrees, scroll / pinch = zoom.
 * Countries (and, once one is opened, its states) rise out of the globe in proportion to the
 * share of material sent there; cities and customers stand up as bars of the same kind.
 * Clicking a country / state presses it down, the camera flies in, and the next level opens.
 * All data and navigation live in SalesMap — this component only draws and reports clicks.
 */

const OCEAN = '#0a1a2f';
const LAND_NO_SALES = '#2a425e';
const DIM = '#22384f';
const SELECTED = '#3b6ea5';
const CITY = '#ffb703';
const CITY_APPROX = '#fcd88a';
const CUSTOMER = '#2ec4b6';
const RAMP = [[31, 95, 139], [53, 181, 172], [242, 193, 78]]; // steel blue -> teal -> gold

function rampColor(t) {
  const x = Math.min(1, Math.max(0, t)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(x));
  const f = x - i;
  const c = RAMP[i].map((v, k) => Math.round(v + (RAMP[i + 1][k] - v) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}
const RAMP_CSS = `linear-gradient(90deg, ${rampColor(0)}, ${rampColor(0.5)}, ${rampColor(1)})`;

// Cheap point thinning: drops vertices closer than `tol` degrees to the last kept one.
// Keeps the globe light (fast first load, smooth rotation) without visibly changing shapes.
const thinCache = new WeakMap();
function thinRing(ring, tol) {
  const out = [ring[0]];
  let last = ring[0];
  const t2 = tol * tol;
  for (let i = 1; i < ring.length - 1; i += 1) {
    const dx = ring[i][0] - last[0];
    const dy = ring[i][1] - last[1];
    if (dx * dx + dy * dy >= t2) { out.push(ring[i]); last = ring[i]; }
  }
  out.push(ring[ring.length - 1]);
  return out.length >= 4 ? out : null;
}
function thinPolygon(rings, tol) {
  const outer = thinRing(rings[0], tol);
  if (!outer) return null;
  const thinned = [outer].concat(rings.slice(1).map((r) => thinRing(r, tol)).filter(Boolean));
  // safety net: thinning must not change the shape's size or flip its winding
  const before = geoArea({ type: 'Polygon', coordinates: rings });
  const after = geoArea({ type: 'Polygon', coordinates: thinned });
  if (Math.abs(after - before) > 0.25 * before + 1e-5) return rings;
  return thinned;
}
function thinGeometry(geom, tol) {
  if (!geom) return geom;
  let byTol = thinCache.get(geom);
  if (!byTol) { byTol = new Map(); thinCache.set(geom, byTol); }
  if (byTol.has(tol)) return byTol.get(tol);
  let out = geom;
  try {
    if (geom.type === 'Polygon') {
      const c = thinPolygon(geom.coordinates, tol);
      if (c) out = { type: 'Polygon', coordinates: c };
    } else if (geom.type === 'MultiPolygon') {
      const c = geom.coordinates.map((p) => thinPolygon(p, tol)).filter(Boolean);
      if (c.length) out = { type: 'MultiPolygon', coordinates: c };
    }
  } catch (e) { /* keep original */ }
  byTol.set(tol, out);
  return out;
}

export function webglSupported() {
  try {
    const c = document.createElement('canvas');
    return Boolean(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) {
    return false;
  }
}

// Some outline files wind their rings the "wrong" way round, which makes a state read as
// "everything except the state" (a huge disc covering the globe). Flip those back.
const fixedCache = new WeakMap();
function fixWinding(geom) {
  if (!geom) return geom;
  if (fixedCache.has(geom)) return fixedCache.get(geom);
  let out = geom;
  try {
    if (geoArea({ type: 'Feature', geometry: geom }) > 2 * Math.PI) {
      const rev = (rings) => rings.map((r) => r.slice().reverse());
      if (geom.type === 'Polygon') out = { type: 'Polygon', coordinates: rev(geom.coordinates) };
      else if (geom.type === 'MultiPolygon') out = { type: 'MultiPolygon', coordinates: geom.coordinates.map(rev) };
    }
  } catch (e) { /* keep as is */ }
  fixedCache.set(geom, out);
  return out;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const frac = (pct, max) => (max > 0 ? clamp(pct / max, 0, 1) : 0);

function darker(rgb) {
  const m = /rgb\((\d+),(\d+),(\d+)\)/.exec(rgb);
  if (!m) return '#475569';
  return `rgb(${Math.round(m[1] * 0.6)},${Math.round(m[2] * 0.6)},${Math.round(m[3] * 0.65)})`;
}

const tip = (name, line) => `<div style="font:600 12px system-ui,sans-serif;padding:4px 6px"><div>${name}</div><div style="font-weight:400">${line}</div></div>`;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
}[ch]));

export default function SalesGlobe({
  level, isIndia, worldFeatures, countryByIso, maxCountryPct, selectedIso2,
  indiaFeatures, stateByName, maxStatePct, normalizeStateName, selectedState,
  cityPoints, showCities, maxCityPct, customerPoints, maxCustomerQty, focus,
  onCountry, onState, onCity, onUnsupported,
}) {
  const boxRef = useRef(null);
  const globeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [pressed, setPressed] = useState(null);
  const handlers = useRef({});
  handlers.current = { onCountry, onState, onCity };
  const timer = useRef(null);
  const touched = useRef(false);

  // ---- create the globe once ------------------------------------------------
  useEffect(() => {
    let dead = false;
    let ro = null;
    let io = null;
    const box = boxRef.current;
    import('globe.gl').then((mod) => {
      if (dead || !box) return;
      const Globe = mod.default || mod;
      let g;
      try {
        g = Globe()(box)
          .backgroundColor('rgba(0,0,0,0)')
          .showAtmosphere(true)
          .atmosphereColor('#4f8fd8')
          .atmosphereAltitude(0.16)
          .polygonsTransitionDuration(0)
          .polygonStrokeColor(() => 'rgba(148,163,184,0.28)')
          .pointResolution(14)
          .labelResolution(2)
          .labelDotRadius(0)
          .labelIncludeDot(false);
      } catch (e) {
        if (onUnsupported) onUnsupported(e);
        return;
      }
      const mat = g.globeMaterial();
      if (mat && mat.color && mat.color.set) mat.color.set(OCEAN);
      if (mat && mat.emissive && mat.emissive.set) { mat.emissive.set('#0a1a2b'); mat.emissiveIntensity = 0.6; }
      const ctl = g.controls();
      ctl.autoRotate = true;
      ctl.autoRotateSpeed = 0.45;
      ctl.enableDamping = true;
      ctl.dampingFactor = 0.09;
      ctl.rotateSpeed = 0.55;
      ctl.zoomSpeed = 0.7;
      ctl.minDistance = 103;
      ctl.maxDistance = 520;
      try { g.renderer().setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5)); } catch (e) { /* ignore */ }
      ctl.addEventListener('start', () => { touched.current = true; ctl.autoRotate = false; });
      const size = () => {
        const w = box.clientWidth || 600;
        g.width(w).height(clamp(Math.round(w * 0.7), 380, 640));
      };
      size();
      if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(size); ro.observe(box); }
      g.pointOfView({ lat: 22, lng: 78, altitude: 2.4 }, 0);
      // stop drawing frames while the globe is off-screen or the tab is hidden
      if (typeof IntersectionObserver !== 'undefined') {
        io = new IntersectionObserver(([en]) => { if (globeRef.current) { if (en.isIntersecting) g.resumeAnimation(); else g.pauseAnimation(); } });
        io.observe(box);
      }
      globeRef.current = g;
      setReady(true);
    }).catch((e) => { if (!dead && onUnsupported) onUnsupported(e); });
    return () => {
      dead = true;
      if (ro) ro.disconnect();
      if (io) io.disconnect();
      clearTimeout(timer.current);
      const g = globeRef.current;
      globeRef.current = null;
      if (g) {
        try { if (g._destructor) g._destructor(); } catch (e) { /* ignore */ }
      }
      if (box) box.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Press the region down, then open it. Used for clicks on the land and on its text label.
  const activate = useCallback((d) => {
    if (!d || !d.stat) return;
    setPressed(d.key);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPressed(null);
      if (d.kind === 'country') handlers.current.onCountry(d.stat);
      else handlers.current.onState(d.stat);
    }, 420);
  }, []);

  // ---- polygons: countries (world) or states (inside India) ------------------
  const polys = useMemo(() => {
    const out = [];
    const atWorld = level === 'world';
    worldFeatures.forEach((f) => {
      const iso = f.properties.iso2;
      const stat = iso ? countryByIso.get(iso) : null;
      if (!atWorld && isIndia && iso === selectedIso2) return; // replaced by its states
      out.push({
        geometry: fixWinding(thinGeometry(f.geometry, !atWorld && iso === selectedIso2 ? 0.08 : 0.3)), kind: 'country', key: `c-${iso || f.properties.name}`, name: stat ? stat.country : f.properties.name, stat, iso,
      });
    });
    if (!atWorld && isIndia) {
      indiaFeatures.forEach((f) => {
        const stat = stateByName.get(normalizeStateName(f.properties.name));
        out.push({
          geometry: fixWinding(thinGeometry(f.geometry, 0.04)), kind: 'state', key: `s-${f.properties.name}`, name: stat ? stat.state : f.properties.name, stat, feature: f,
        });
      });
    }
    return out;
  }, [level, isIndia, worldFeatures, indiaFeatures, countryByIso, stateByName, selectedIso2, normalizeStateName]);

  const altOf = useMemo(() => (d) => {
    const lift = 0;
    if (pressed === d.key) return 0.002;
    const atWorld = level === 'world';
    if (d.kind === 'country') {
      if (atWorld) return d.stat ? 0.012 + 0.17 * frac(d.stat.pct, maxCountryPct) ** 0.85 + lift : 0.0015;
      if (d.iso === selectedIso2) return 0.02 + lift;
      return 0.0015;
    }
    // state of India
    if (level === 'state') return d.stat ? 0.006 + 0.075 * frac(d.stat.pct, maxStatePct) ** 0.85 + lift : 0.002;
    const sel = selectedState && normalizeStateName(selectedState) === normalizeStateName(d.name);
    return sel ? 0.012 : 0.002;
  }, [pressed, level, maxCountryPct, maxStatePct, selectedIso2, selectedState, normalizeStateName]);

  const capOf = useMemo(() => (d) => {
    const atWorld = level === 'world';
    if (d.kind === 'country') {
      if (atWorld) return d.stat ? rampColor(frac(d.stat.pct, maxCountryPct)) : LAND_NO_SALES;
      return d.iso === selectedIso2 ? SELECTED : DIM;
    }
    if (level === 'state') return d.stat ? rampColor(frac(d.stat.pct, maxStatePct)) : LAND_NO_SALES;
    const sel = selectedState && normalizeStateName(selectedState) === normalizeStateName(d.name);
    return sel ? SELECTED : DIM;
  }, [level, maxCountryPct, maxStatePct, selectedIso2, selectedState, normalizeStateName]);

  useEffect(() => {
    const g = globeRef.current;
    if (!ready || !g) return;
    g.polygonsData(polys)
      .polygonCapColor(capOf)
      .polygonSideColor((d) => (capOf(d).startsWith('rgb') ? darker(capOf(d)) : '#16293d'))
      .polygonAltitude(altOf)
      .polygonLabel((d) => (d.stat
        ? tip(esc(d.name), `${d.stat.pct}% of total sales`)
        : (level === 'world' || d.kind === 'state' ? tip(esc(d.name), 'no dispatches') : '')))
      .onPolygonHover((d) => { if (boxRef.current) boxRef.current.style.cursor = d && d.stat ? 'pointer' : 'grab'; })
      .onPolygonClick(activate);
  }, [ready, polys, capOf, altOf, level, activate]);

  // ---- bars: cities, customers -----------------------------------------------
  const viewAlt = focus && focus.alt ? focus.alt : 1;
  const viewSize = viewAlt * 50;
  const selectedStateAlt = level === 'customer' ? 0 : (isIndia ? 0.012 : 0.02);
  const bars = useMemo(() => {
    const out = [];
    if (showCities || level === 'customer') {
      cityPoints.forEach((c) => {
        const f = frac(c.pct, maxCityPct);
        out.push({
          kind: 'city', lat: c.lat, lng: c.lng, ref: c, name: c.cityLabel || c.city,
          h: level === 'customer' ? 0.002 : selectedStateAlt + viewAlt * 0.05 + viewAlt * 0.3 * f,
          r: level === 'customer' ? viewSize * 0.003 : viewSize * 0.011 * (0.7 + 1.3 * f),
          color: c.approximate ? CITY_APPROX : CITY,
          tip: tip(esc(c.cityLabel || c.city), `${c.pct}% of total sales${c.approximate ? ' (approximate position)' : ''}`),
        });
      });
    }
    if (level === 'customer') {
      customerPoints.forEach((c) => {
        const f = frac(c.qty, maxCustomerQty);
        out.push({
          kind: 'customer', lat: c.lat, lng: c.lng, ref: c, name: c.customer,
          h: viewAlt * 0.05 + viewAlt * 0.35 * f, r: viewSize * 0.011 * (0.7 + 1.3 * f), color: CUSTOMER,
          tip: tip(esc(c.customer), `${c.qty} Box`),
        });
      });
    }
    return out;
  }, [level, showCities, cityPoints, customerPoints, maxCityPct, maxCustomerQty, viewAlt, viewSize, selectedStateAlt]);

  useEffect(() => {
    const g = globeRef.current;
    if (!ready || !g) return;
    g.pointsData(bars)
      .pointLat('lat').pointLng('lng')
      .pointAltitude('h').pointRadius('r').pointColor('color')
      .pointLabel('tip')
      .pointsMerge(false)
      .onPointClick((d) => { if (d && d.kind === 'city' && level === 'city') handlers.current.onCity(d.ref); });
  }, [ready, bars, level]);

  // ---- text labels -------------------------------------------------------------
  const labels = useMemo(() => {
    const out = [];
    const textFor = (txt, color, lat, lng, alt, size, act) => out.push({
      lat, lng, text: txt, color, alt, size, act,
    });
    if (level === 'world') {
      polys.forEach((d) => {
        if (!d.stat) return;
        const [lng, lat] = geoCentroid({ type: 'Feature', geometry: d.geometry });
        if (Number.isNaN(lat)) return;
        const f = frac(d.stat.pct, maxCountryPct);
        textFor(`${d.name} ${d.stat.pct}%`, '#f8fafc', lat, lng, 0.012 + 0.17 * f ** 0.85 + 0.004, 1.15, () => activate(d));
      });
    } else if (level === 'state' && isIndia) {
      polys.forEach((d) => {
        if (d.kind !== 'state' || !d.stat) return;
        const [lng, lat] = geoCentroid({ type: 'Feature', geometry: d.geometry });
        if (Number.isNaN(lat)) return;
        const f = frac(d.stat.pct, maxStatePct);
        textFor(`${d.stat.pct}%`, '#ffffff', lat, lng, 0.006 + 0.075 * f ** 0.85 + 0.003, 0.4, () => activate(d));
      });
    } else if (showCities && bars.length <= 12) {
      bars.forEach((b) => textFor(`${b.name} (${b.ref.pct}%)`, '#ffffff', b.lat, b.lng, b.h + 0.004, viewSize * 0.016, () => handlers.current.onCity(b.ref)));
    } else if (level === 'customer' && bars.length <= 14) {
      bars.filter((b) => b.kind === 'customer').forEach((b) => textFor(`${b.name} (${b.ref.qty} Box)`, '#ffffff', b.lat, b.lng, b.h + 0.002, viewSize * 0.016));
    }
    return out;
  }, [level, isIndia, showCities, polys, bars, maxCountryPct, maxStatePct, viewSize, activate]);

  useEffect(() => {
    const g = globeRef.current;
    if (!ready || !g) return;
    g.labelsData(labels)
      .labelLat('lat').labelLng('lng').labelText('text').labelColor('color')
      .labelAltitude('alt').labelSize('size')
      .onLabelClick((l) => { if (l && l.act) l.act(); });
  }, [ready, labels]);

  // ---- camera ----------------------------------------------------------------
  useEffect(() => {
    const g = globeRef.current;
    if (!ready || !g || !focus) return;
    const ctl = g.controls();
    ctl.autoRotate = level === 'world' && !touched.current;
    g.pointOfView({ lat: focus.lat, lng: focus.lng, altitude: focus.alt }, 1300);
  }, [ready, focus, level]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        ref={boxRef}
        data-testid="sales-globe"
        style={{
          width: '100%', minHeight: 380, borderRadius: 'var(--radius-sm)', overflow: 'hidden', cursor: 'grab',
          background: 'radial-gradient(ellipse at 50% 40%, #10243c 0%, #050b14 78%)',
        }}
      />
      <div style={{
        position: 'absolute', left: 10, bottom: 8, fontSize: 11, color: '#cbd5e1', pointerEvents: 'none', textShadow: '0 1px 2px #000',
      }}
      >
        Drag to rotate · scroll / pinch to zoom · taller = more material sent · click to open
      </div>
      {(level === 'world' || (level === 'state' && isIndia)) && (
        <div style={{
          position: 'absolute', right: 10, bottom: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#cbd5e1', pointerEvents: 'none',
        }}
        >
          <span>0%</span>
          <span style={{
            width: 110, height: 8, borderRadius: 4, background: RAMP_CSS, border: '1px solid rgba(255,255,255,0.25)',
          }}
          />
          <span>{`${Math.round((level === 'world' ? maxCountryPct : maxStatePct) * 10) / 10}%`}</span>
        </div>
      )}
      {!ready && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#cbd5e1', fontSize: 13 }}>Loading globe…</div>}
    </div>
  );
}
